// Canonical API-error → user-facing copy resolver. The wire envelope and
// `DomainError` both carry a stable machine code (e.g. `UNAUTHENTICATED`,
// `identity/photo_invalid`); `DomainError.message` is set to that code and
// the envelope's own `message` is English server prose, so neither is ever
// rendered. Every error-display site resolves the code through the active
// locale's `Errors` catalog here, falling back to the call site's localized
// copy when the code has none.
//
// This is a hook on purpose: copy is resolved where it renders. Code without
// React context (mutation files, module-scope helpers) carries the thrown
// error or its code to the component instead of pre-rendering a string.

import { useCallback } from 'react';
import { useTranslations } from 'use-intl';
import errorCopy from '../../messages/en/errors.json';
import { apiErrorCode } from './api-error';

type ErrorCode = keyof typeof errorCopy;

// English is the key-set source of truth: every non-default catalog is
// deep-merged over it (see `messages/load.ts`), so a key present here
// resolves in every locale.
function isErrorCode(code: string): code is ErrorCode {
  return Object.hasOwn(errorCopy, code);
}

/**
 * Resolves a thrown API error (or `{ code }` / `{ error: { code } }` shape)
 * to localized copy. Unknown codes and code-less errors return `fallback`
 * (default: the generic `UNKNOWN` message).
 */
export type DescribeError = (err: unknown, fallback?: string) => string;

export function useErrorMessage(): DescribeError {
  const t = useTranslations('Errors');
  return useCallback(
    (err: unknown, fallback?: string): string => {
      const code = apiErrorCode(err);
      if (code !== null && isErrorCode(code)) return t(code);
      return fallback ?? t('UNKNOWN');
    },
    [t],
  );
}
